import { useEffect, useMemo, useState } from 'react';
import { availabilityLabel, verificationLabel } from '../lib/format.js';

const filters = [
  ['all', 'All'],
  ['rwa', 'RWA'],
  ['stablecoin', 'Stable'],
  ['gateway', 'Gateway'],
];

export function AssetDirectory({
  programs,
  selectedId,
  onSelect,
  scope,
  onScopeChange,
  discoveryCount,
  curatedCount,
  catalogCount,
  catalogStatus,
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const availableFilters = useMemo(() => filters.filter(([value]) => (
    value === 'all' || programs.some((program) => program.category === value)
  )), [programs]);

  useEffect(() => {
    if (!availableFilters.some(([value]) => value === filter)) setFilter('all');
  }, [availableFilters, filter]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return programs.filter((program) => {
      if (filter !== 'all' && program.category !== filter) return false;
      if (!needle) return true;
      return [
        program.name,
        program.ticker,
        program.hq?.country,
        program.jurisdiction,
        program.summary,
        program.catalog?.platform,
        program.catalog?.assetClass,
        ...(program.instruments || []).flatMap((instrument) => [instrument.issuer, instrument.currency, instrument.issuanceId]),
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle));
    });
  }, [filter, programs, query]);

  return (
    <section className="directory panel" aria-labelledby="directory-title">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Coverage</span>
          <h2 id="directory-title">Asset registry</h2>
        </div>
        <span className="count-pill" aria-label={`${visible.length} results`}>{visible.length}</span>
      </div>

      <div className="scope-switch" role="group" aria-label="Registry evidence scope">
        <button className={scope === 'curated' ? 'active' : ''} aria-pressed={scope === 'curated'} onClick={() => onScopeChange('curated')}>
          Curated <span>{curatedCount}</span>
        </button>
        <button className={scope === 'catalog' ? 'active' : ''} aria-pressed={scope === 'catalog'} disabled={catalogStatus === 'error' && scope !== 'catalog'} title={catalogStatus === 'error' ? 'Authorized catalog feed not connected' : undefined} onClick={() => onScopeChange('catalog')}>
          RWA.xyz <span>{catalogStatus === 'loading' ? '…' : catalogStatus === 'error' ? '—' : catalogCount}</span>
        </button>
        <button className={scope === 'metadata' ? 'active' : ''} aria-pressed={scope === 'metadata'} onClick={() => onScopeChange('metadata')}>
          MPT <span>{discoveryCount || 0}</span>
        </button>
      </div>
      {scope === 'catalog' && (
        <p className="context-note catalog-note">Dated RWA.xyz reference records. Exact pairs join live matching; catalog inclusion is not first-party verification.</p>
      )}
      {scope === 'metadata' && (
        <p className="context-note warning-note">Self-described MPT records. Metadata is not proof of issuer, backing or legal rights.</p>
      )}

      <label className="search-field">
        <span className="sr-only">Search asset registry</span>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 21-4.4-4.4m2.4-5.1a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" /></svg>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search asset, issuer, country" />
      </label>

      <div className="filter-row" role="group" aria-label="Asset category">
        {availableFilters.map(([value, label]) => (
          <button key={value} className={filter === value ? 'active' : ''} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>
        ))}
      </div>

      <div className="directory-list">
        {visible.map((program) => (
          <button
            type="button"
            className={`directory-card ${selectedId === program.id ? 'selected' : ''}`}
            key={program.id}
            onClick={() => onSelect(program.id)}
            aria-pressed={selectedId === program.id}
          >
            <span className={`asset-monogram category-${program.category}`}>{program.ticker.slice(0, 3)}</span>
            <span className="directory-card-copy">
              <strong>{program.name}</strong>
              <small>{program.catalog?.platform || program.ticker} · {program.hq?.country || program.jurisdiction || 'Location undisclosed'}</small>
              <span className={`evidence-line verification-${program.verification}`}>
                {verificationLabel(program.verification)}
              </span>
            </span>
            <span className={`status-pin status-${program.availability}`} title={availabilityLabel(program.availability)} aria-label={availabilityLabel(program.availability)} />
          </button>
        ))}
        {!visible.length && <div className="empty-state">No assets match this view.</div>}
      </div>
    </section>
  );
}
